import { useState } from "react";
import { Button, Loader } from "@src/components/atoms";
import AdminHooks from "@src/context/hooks/adminHooks";
import { useAuthContext } from "@src/context";
import { dateFormatterUTC } from "@src/constants/convertions";
import { TiptapEditor } from "@src/components/atoms/RichTextEditor";

const OverridesForm = ({ initial = {}, onSave, onCancel, submitting }) => {
  const [description, setDescription] = useState(initial.description ?? "");
  const [terminology, setTerminology] = useState(initial.terminology ?? "");
  const [reasons, setReasons] = useState(initial.reasons ?? "");
  const [fuelSavingsSource, setFuelSavingsSource] = useState(
    initial.fuelSavingsSource ?? "",
  );

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <span className="text-xs text-gray-500">Description</span>
        <textarea
          className="w-full text-sm border border-neutral-200 rounded p-2 h-24"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter group description for this customer…"
        />
      </div>
      <div className="space-y-1">
        <span className="text-xs text-gray-500">Terminology</span>
        <TiptapEditor value={terminology} onChange={setTerminology} />
      </div>
      <div className="space-y-1">
        <span className="text-xs text-gray-500">Reasons</span>
        <textarea
          className="w-full text-sm border border-neutral-200 rounded p-2 h-24"
          value={reasons}
          onChange={(e) => setReasons(e.target.value)}
          placeholder="Enter reasons…"
        />
      </div>
      <div className="space-y-1">
        <span className="text-xs text-gray-500">Fuel Savings Source</span>
        <textarea
          className="w-full text-sm border border-neutral-200 rounded p-2 h-24"
          value={fuelSavingsSource}
          onChange={(e) => setFuelSavingsSource(e.target.value)}
          placeholder="Enter fuel savings source…"
        />
      </div>
      <div className="flex gap-2">
        <Button
          submit={false}
          loading={submitting}
          onClick={() =>
            onSave({ description, terminology, reasons, fuelSavingsSource })
          }
        >
          Save
        </Button>
        <Button secondary onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export const CustomerBehaviourGroupsSection = ({ customerId }) => {
  const auth = useAuthContext();
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const { data: globalGroups = [], isLoading: groupsLoading } =
    AdminHooks.useGetBehaviourGroups(auth.activeRole);

  const { data: customerGroups = [], isLoading: customerGroupsLoading } =
    AdminHooks.useGetCustomerBehaviourGroups(auth.activeRole, customerId);

  const createMutation = AdminHooks.useCreateCustomerBehaviourGroup();
  const updateMutation = AdminHooks.useUpdateCustomerBehaviourGroup();
  const deleteMutation = AdminHooks.useDeleteCustomerBehaviourGroup();

  const isLoading = groupsLoading || customerGroupsLoading;

  const customerGroupByBehaviourGroupId = (groupId) =>
    customerGroups.find((cg) => cg.behaviourGroupId === groupId);

  const handleSave = async (globalGroup, overrides) => {
    setSubmitting(true);
    setError("");
    setSubmitted(false);
    const existing = customerGroupByBehaviourGroupId(globalGroup.id);
    const payload = {
      description: overrides.description || null,
      terminology: overrides.terminology || null,
      reasons: overrides.reasons || null,
      fuelSavingsSource: overrides.fuelSavingsSource || null,
    };
    try {
      if (existing) {
        await updateMutation.mutateAsync({
          id: existing.id,
          behaviourGroupId: globalGroup.id,
          ...payload,
        });
      } else {
        await createMutation.mutateAsync({
          customerId,
          behaviourGroupId: globalGroup.id,
          ...payload,
        });
      }
      setSubmitted(true);
      setEditingGroupId(null);
    } catch {
      setError("Failed to save.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (customerGroup) => {
    if (
      !window.confirm(`Remove the overrides for "${customerGroup.groupName}"?`)
    )
      return;
    setError("");
    setSubmitted(false);
    try {
      await deleteMutation.mutateAsync(customerGroup.id);
      setSubmitted(true);
    } catch {
      setError("Failed to delete.");
    }
  };

  return (
    <div className="p-4">
      <div className="pb-3">
        <h3 className="font-bold text-lg">Customer Group Descriptions</h3>
        <p className="text-sm text-gray-500">
          Per-customer overrides for each behaviour group.
        </p>
      </div>

      {!!submitted && (
        <div className="mb-2 text-green-600 border border-green-300 rounded-xl p-2 bg-green-50 text-sm">
          Changes saved successfully.
        </div>
      )}
      {!!error && (
        <div className="mb-2 text-red-600 border border-red-300 rounded-xl p-2 bg-red-50 text-sm">
          {error}
        </div>
      )}

      {isLoading && <Loader size="2xl" />}

      {!isLoading && (
        <div className="space-y-3">
          {globalGroups.length === 0 && (
            <div className="text-gray-400 text-sm italic">
              No behaviour groups defined globally.
            </div>
          )}
          {globalGroups.map((group) => {
            const customerGroup = customerGroupByBehaviourGroupId(group.id);
            const isEditing = editingGroupId === group.id;

            return (
              <div
                key={group.id}
                className="border border-neutral-200 rounded-lg p-4 space-y-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="font-bold">{group.name}</span>
                    <span className="ml-2 text-xs text-gray-400 font-mono">
                      {group.groupCode}
                    </span>
                    {customerGroup?.createdAt && (
                      <span className="ml-2 text-xs text-gray-400">
                        · created {dateFormatterUTC(customerGroup.createdAt)}
                      </span>
                    )}
                  </div>
                  {!isEditing && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        icon="feedback"
                        block={false}
                        secondary
                        onClick={() => setEditingGroupId(group.id)}
                      >
                        {customerGroup ? "Edit" : "Add overrides"}
                      </Button>
                      {customerGroup && (
                        <Button
                          icon="close"
                          color="red"
                          block={false}
                          secondary
                          onClick={() => handleDelete(customerGroup)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {!isEditing && customerGroup && (
                  <div className="space-y-2 text-sm text-gray-600">
                    {customerGroup.description && (
                      <div>
                        <span className="text-xs text-gray-400 font-medium">
                          Description:{" "}
                        </span>
                        {customerGroup.description}
                      </div>
                    )}
                    {customerGroup.terminology && (
                      <div>
                        <span className="text-xs text-gray-400 font-medium">
                          Terminology:{" "}
                        </span>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: customerGroup.terminology,
                          }}
                        />
                      </div>
                    )}
                    {customerGroup.reasons && (
                      <div>
                        <span className="text-xs text-gray-400 font-medium">
                          Reasons:{" "}
                        </span>
                        {customerGroup.reasons}
                      </div>
                    )}
                    {customerGroup.fuelSavingsSource && (
                      <div>
                        <span className="text-xs text-gray-400 font-medium">
                          Fuel Savings Source:{" "}
                        </span>
                        {customerGroup.fuelSavingsSource}
                      </div>
                    )}
                    {!customerGroup.description &&
                      !customerGroup.terminology &&
                      !customerGroup.reasons &&
                      !customerGroup.fuelSavingsSource && (
                        <span className="italic text-gray-400">
                          No overrides set
                        </span>
                      )}
                  </div>
                )}

                {!isEditing && !customerGroup && (
                  <div className="text-sm">
                    <span className="italic text-gray-400">
                      No overrides set
                    </span>
                  </div>
                )}

                {isEditing && (
                  <OverridesForm
                    initial={customerGroup ?? {}}
                    submitting={submitting}
                    onSave={(overrides) => handleSave(group, overrides)}
                    onCancel={() => setEditingGroupId(null)}
                  />
                )}

                <div className="grid grid-cols-2 gap-x-4 text-xs text-gray-400 pt-1">
                  <div>
                    <span className="font-medium">Record ID:</span>{" "}
                    <span className="font-mono">
                      {customerGroup?.id ?? (
                        <span className="italic">not created</span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Behaviour Group ID:</span>{" "}
                    <span className="font-mono">{group.id}</span>
                  </div>
                  <div>
                    <span className="font-medium">Customer ID:</span>{" "}
                    <span className="font-mono">
                      {customerGroup?.customerId ?? (
                        <span className="italic">not created</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
